/**
 * The loaders' reads of a conversation — `W4-T06` §3.6.
 *
 * `fetchQuery` with `staleTime: 0`, deliberately unlike every other loader in this app: this is the
 * read the poll (`poll.ts`) exists to repeat, so it must reach the server each time the loader runs.
 * The rest of the page keeps `ensureQueryData` and is served from the cache.
 *
 * A failure degrades to `null` rather than failing the page: a provider who cannot load the
 * conversation can still quote, and a client can still decide.
 */
import { type JobThreadList, type JobThreadView } from '@marketplace/contracts';
import { type RouterContextProvider } from 'react-router';
import { queryKeys, routeContext } from '../../shared/query.js';

type Context = Readonly<RouterContextProvider>;

async function orNull<T>(read: () => Promise<T>): Promise<T | null> {
  try {
    return await read();
  } catch {
    return null;
  }
}

export function loadMyThread(context: Context, jobId: string): Promise<JobThreadView | null> {
  const { queryClient, api } = context.get(routeContext);
  return orNull(() =>
    queryClient.fetchQuery({
      queryKey: queryKeys.myThread(jobId),
      queryFn: () => api.getMyThread(jobId),
      staleTime: 0,
    }),
  );
}

export function loadJobThreads(context: Context, jobId: string): Promise<JobThreadList | null> {
  const { queryClient, api } = context.get(routeContext);
  return orNull(() =>
    queryClient.fetchQuery({
      queryKey: queryKeys.jobThreads(jobId),
      queryFn: () => api.getJobThreads(jobId),
      staleTime: 0,
    }),
  );
}

export function loadJobThread(
  context: Context,
  jobId: string,
  threadId: string,
): Promise<JobThreadView | null> {
  const { queryClient, api } = context.get(routeContext);
  return orNull(() =>
    queryClient.fetchQuery({
      queryKey: queryKeys.jobThread(jobId, threadId),
      queryFn: () => api.getJobThread(jobId, threadId),
      staleTime: 0,
    }),
  );
}
